/**
 * 로그인 오류를 읽고 정리한다.
 * @version 1.0.0
 * @created 2026-02-04
 */

import { ERROR_MESSAGE_MAP, DEFAULT_ERROR_MESSAGE } from "./errors";

/**
 * URL에서 인증 오류를 읽는다.
 * @returns Error code and description.
 */
export function extractErrorFromURL(): {
  error: string | null;
  errorDescription: string | null;
} {
  // Read query parameters.
  const searchParams = new URLSearchParams(window.location.search);

  // Read hash parameters.
  const hashParams = new URLSearchParams(window.location.hash.substring(1));

  const error = searchParams.get("error") || hashParams.get("error");
  const errorDescription = searchParams.get("error_description") || hashParams.get("error_description");

  return { error, errorDescription };
}

/**
 * 인증 오류를 안내 문구로 변환한다.
 * @param error Error code or message.
 * @param errorDescription Error description.
 * @returns Display message or null.
 */
export function mapErrorToMessage(
  error: string | null, 
  errorDescription: string | null
): string | null {
  // Return null when no error is present.
  if (!error && !errorDescription) {
    return null;
  }

  // Prefer the error description.
  const errorKey = errorDescription || error;

  if (!errorKey) {
    return null;
  }

  // Look up the display message.
  const mappedMessage = ERROR_MESSAGE_MAP[errorKey];

  if (mappedMessage) {
    return mappedMessage;
  }

  // Preserve Korean messages; otherwise use the default.
  const hasKorean = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(errorKey);

  return hasKorean ? errorKey : DEFAULT_ERROR_MESSAGE;
}

/** URL의 인증 오류를 정리한다. */
export function cleanErrorFromURL(): void {
  const searchParams = new URLSearchParams(window.location.search);

  // Remove error parameters.
  searchParams.delete("error");
  searchParams.delete("error_description");

  // Preserve other query parameters.
  const cleanUrl = searchParams.toString() ? `${window.location.pathname}?${searchParams.toString()}` : window.location.pathname;

  window.history.replaceState({}, document.title, cleanUrl);
}

/**
 * 쿠키에서 오류 메시지를 읽는다.
 * @returns Cookie message or null.
 */
export function extractErrorFromCookie(): string | null {
  const cookies = document.cookie.split("; ");
  const errorCookie = cookies.find((c) => c.startsWith("bz-error="));

  if (!errorCookie) {
    return null;
  }

  const value = errorCookie.split("=")[1];

  return value ? decodeURIComponent(value) : null;
}

/** 오류 쿠키를 삭제한다. */
export function cleanErrorCookie(): void {
  document.cookie = "bz-error=; path=/signin; max-age=0";
}
